import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

const DEFAULT_DROPDOWN_TIMEOUT_MS = 30000;

export type ReactiveDropdownFetchContext = {
  readonly endpoint: unknown;
  readonly params: Record<string, unknown>;
  readonly timeoutMs?: number;
  readonly retryAttempts?: number;
  readonly retryDelayMs?: number;
};

export type ReactiveDropdownFetchResult = {
  readonly response: unknown;
  readonly retryCount: number;
};

type ReactiveDropdownEndpoint = {
  readonly toPromise?: (params?: Record<string, unknown>) => Promise<unknown>;
  readonly perform?: (params?: Record<string, unknown>) => unknown;
  readonly response?: (callback: (value: unknown) => void) => unknown;
  readonly getRequestData?: () => unknown;
  readonly hasError?: boolean;
};

type ReactiveDropdownLoaderConfig = {
  readonly http: HttpClient;
  readonly getServerUrl: () => string;
  readonly resolveTechnicalErrorMessage: (error: unknown) => string;
};

export class ReactiveDropdownLoader {

  public constructor(private readonly config: ReactiveDropdownLoaderConfig) {}

  public async fetch(context: ReactiveDropdownFetchContext): Promise<ReactiveDropdownFetchResult> {
    const maxAttempts = Math.max(1, context.retryAttempts ?? 1);
    const retryDelayMs = Math.max(0, context.retryDelayMs ?? 0);
    let lastError: unknown = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        return {
          response: await this.fetchAttempt(context.endpoint, context.params, context.timeoutMs),
          retryCount: attempt - 1,
        };
      } catch (error: unknown) {
        lastError = error;

        if (attempt < maxAttempts && retryDelayMs > 0) {
          await this.wait(retryDelayMs);
        }
      }
    }

    if (lastError instanceof Error) {
      throw lastError;
    }

    throw new Error(this.config.resolveTechnicalErrorMessage(lastError) || 'Dropdown request failed.');
  }

  private async fetchAttempt(endpoint: unknown, params: Record<string, unknown>, timeoutMs: number | undefined): Promise<unknown> {
    let requestPromise: Promise<unknown>;

    if (typeof endpoint === 'function') {
      requestPromise = Promise.resolve(endpoint(params));
    } else if (typeof endpoint === 'string') {
      requestPromise = firstValueFrom(this.config.http.get(this.toDataSourceUrl(endpoint), { params: this.toHttpQueryParams(params) }));
    } else {
      requestPromise = this.fetchRequestHandlerData(endpoint, params);
    }

    return this.withTimeout(requestPromise, timeoutMs);
  }

  private async fetchRequestHandlerData(endpoint: unknown, params: Record<string, unknown>): Promise<unknown> {
    const requestEndpoint = endpoint as ReactiveDropdownEndpoint | null;

    if (!requestEndpoint) {
      throw new Error('Unsupported dropdown dataSource endpoint.');
    }

    if (typeof requestEndpoint.toPromise === 'function') {
      return requestEndpoint.toPromise(params);
    }

    if (typeof requestEndpoint.perform === 'function' && typeof requestEndpoint.response === 'function') {
      return new Promise<unknown>((resolve, reject): void => {
        requestEndpoint.response?.((response: unknown): void => {
          if (requestEndpoint.hasError === true) {
            reject(new Error('Dropdown request finished with error.'));

            return;
          }

          resolve(response);
        });

        requestEndpoint.perform?.(params);
      });
    }

    throw new Error('Unsupported dropdown dataSource endpoint.');
  }

  private async wait(delayMs: number): Promise<void> {
    if (delayMs <= 0) {
      return;
    }

    await new Promise<void>((resolve): void => {
      globalThis.setTimeout(resolve, delayMs);
    });
  }

  private async withTimeout(requestPromise: Promise<unknown>, timeoutMs: number | undefined): Promise<unknown> {
    const resolvedTimeout = timeoutMs ?? DEFAULT_DROPDOWN_TIMEOUT_MS;

    if (resolvedTimeout <= 0) {
      return requestPromise;
    }

    return new Promise<unknown>((resolve, reject): void => {
      const timeoutId = globalThis.setTimeout((): void => {
        reject(new Error('Dropdown request timed out.'));
      }, resolvedTimeout);

      requestPromise
        .then((value) => {
          globalThis.clearTimeout(timeoutId);
          resolve(value);
        })
        .catch((error: unknown) => {
          globalThis.clearTimeout(timeoutId);
          reject(error);
        });
    });
  }

  private toDataSourceUrl(endpoint: string): string {
    if (/^https?:\/\//u.test(endpoint)) {
      return endpoint;
    }

    const baseUrl = this.config.getServerUrl();
    const separator = baseUrl.endsWith('/') ? '' : '/';
    const normalizedEndpoint = this.toIndexedDataSourceEndpoint(endpoint, baseUrl);

    return `${baseUrl}${separator}ws/${normalizedEndpoint}`;
  }

  private toIndexedDataSourceEndpoint(endpoint: string, baseUrl: string): string {
    if (endpoint.startsWith('index.php') || baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')) {
      return endpoint;
    }

    const [path, queryString] = endpoint.split('?');
    const parts = path?.split('/').filter((part) => part.trim() !== '');
    const indexedParams = parts?.map((part, index) => `p${index + 1}=${encodeURIComponent(part)}`);

    if (queryString && queryString.trim() !== '' && indexedParams) {
      indexedParams.push(queryString);
    }

    return `index.php?${indexedParams?.join('&')}`;
  }

  private toHttpQueryParams(params: Record<string, unknown>): Record<string, string> {
    const queryParams: Record<string, string> = {};

    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined) {
        queryParams[key] = String(value);
      }
    }

    return queryParams;
  }
}
